import { useEffect } from "react";
import { getPlatformService } from "../../platform";
import { PLATFORM_CONNECTION_CHANGED_EVENT } from "../../platform/engine-proxy";
import { isPreviewWidthValue } from "../../lib/preview-width";
import { useEditorStore } from "../../store/editor-store";
import { getNotificationBus } from "../../store/notification-bus";
import { useSettingsStore } from "../../store/settings-store";
import {
    useThemeStore,
} from "../../theme/runtime/theme-store";
import { waitForEngineReady } from "./settings-center/resolvers";

export function SettingsRuntimeBindings() {
    const snapshot = useSettingsStore((state) => state.snapshot);
    const ensureLoaded = useSettingsStore((state) => state.ensureLoaded);
    const loadSchema = useSettingsStore((state) => state.loadSchema);
    const loadSnapshot = useSettingsStore((state) => state.loadSnapshot);
    const handleConfigChanged = useSettingsStore(
        (state) => state.handleConfigChanged,
    );

    useEffect(() => {
        void ensureLoaded();
    }, [ensureLoaded]);

    useEffect(() => {
        const bus = getNotificationBus();
        return bus.subscribe<{ changedKeys: string[] }>(
            "event/config.changed",
            (params) => {
                void handleConfigChanged(params.changedKeys ?? []);
            },
        );
    }, [handleConfigChanged]);

    useEffect(() => {
        const bus = getNotificationBus();
        return bus.subscribe("event/workspace.foldersChanged", () => {
            void Promise.all([loadSchema(), loadSnapshot()]);
        });
    }, [loadSchema, loadSnapshot]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        let abortController: AbortController | null = null;
        const handleConnectionChanged = () => {
            // Cancel any previous in-flight reload cycle
            abortController?.abort();
            const ac = new AbortController();
            abortController = ac;
            void (async () => {
                const engine = getPlatformService().engine;
                await waitForEngineReady(engine);
                if (ac.signal.aborted) return;
                const bus = getNotificationBus();
                await bus.connect(engine);
                if (ac.signal.aborted) return;
                await reloadSettingsUntilRemoteReady(loadSchema, loadSnapshot, ac.signal);
            })();
        };
        window.addEventListener(
            PLATFORM_CONNECTION_CHANGED_EVENT,
            handleConnectionChanged,
        );
        return () => {
            abortController?.abort();
            window.removeEventListener(
                PLATFORM_CONNECTION_CHANGED_EVENT,
                handleConnectionChanged,
            );
        };
    }, [loadSchema, loadSnapshot]);

    useEffect(() => {
        const v = (key: string) => snapshot?.view.values[key];

        // Editor settings need explicit type narrowing
        const previewWidth = v("editor.defaultPreviewWidth");
        const defaultViewMode = v("editor.defaultViewMode");
        const showInlineDiagnostics = v("editor.showInlineDiagnostics");
        const autoSave = v("editor.autoSave");
        const codeMirrorHoverDetail = v("editor.codeMirror.hoverDetail");
        const codeMirrorWidgetMode = v("editor.codeMirror.widgetMode");

        useEditorStore.getState().applyEditorSettings({
            autoSave: typeof autoSave === "boolean" ? autoSave : false,
            codeMirrorHoverDetail:
                typeof codeMirrorHoverDetail === "boolean"
                    ? codeMirrorHoverDetail
                    : true,
            codeMirrorWidgetMode:
                codeMirrorWidgetMode === "pretty" ||
                    codeMirrorWidgetMode === "raw"
                    ? codeMirrorWidgetMode
                    : "pretty",
            defaultViewMode:
                defaultViewMode === "editor" ||
                    defaultViewMode === "split" ||
                    defaultViewMode === "preview"
                    ? defaultViewMode
                    : undefined,
            defaultPreviewWidth:
                isPreviewWidthValue(previewWidth)
                    ? previewWidth
                    : undefined,
            showInlineDiagnostics:
                typeof showInlineDiagnostics === "boolean"
                    ? showInlineDiagnostics
                    : true,
        });

        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("magic-md:show-inline-diagnostics", {
                    detail: {
                        enabled:
                            typeof showInlineDiagnostics === "boolean"
                                ? showInlineDiagnostics
                                : true,
                    },
                }),
            );
        }

        // Theme settings — ThemeSettingsInput accepts unknown; normalizeThemeSettings handles narrowing
        useThemeStore.getState().applySettings({
            colorTheme: v("editor.appearance.colorTheme"),
            productIconTheme: v("editor.appearance.productIconTheme"),
            fileIconTheme: v("editor.appearance.fileIconTheme"),
            contentProfile: v("editor.preview.contentProfile"),
            presentationProfile: v("editor.publish.presentationProfile"),
        });
    }, [snapshot]);

    return null;
}

async function reloadSettingsUntilRemoteReady(
    loadSchema: () => Promise<void>,
    loadSnapshot: () => Promise<void>,
    signal?: AbortSignal,
): Promise<void> {
    for (let attempt = 0; attempt < 5; attempt += 1) {
        if (signal?.aborted) return;
        await Promise.all([loadSchema(), loadSnapshot()]);

        const { snapshot, schema } = useSettingsStore.getState();
        const targets = snapshot?.capabilities.availableTargets ?? [];
        const hasRemoteBackedSchema = schema.some(
            (entry) =>
                entry.targets.includes("remoteHost") ||
                entry.targets.includes("workspace"),
        );
        if (
            (targets.includes("remoteHost") || targets.includes("workspace")) &&
            hasRemoteBackedSchema
        ) {
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 300));
    }
}
